/**
 * <LivingBackground />
 * ─────────────────────────────────────────────────────────────────────
 * Single source of truth for the animated background layer across the
 * app. Renders different visualisations based on tenant branding:
 *
 *   preset = 'particles'   → gold/violet particle network (default)
 *   preset = 'starfield'   → drifting stars + parallax
 *   preset = 'aurora'      → soft animated colour gradient (CSS-only)
 *   preset = 'mesh'        → moving radial-gradient mesh
 *   preset = 'video'       → operator-provided MP4/WebM loop
 *   preset = 'none'        → returns null
 *
 * Light / day mode (v159+):
 *   The "luxe dark" look used to render NOTHING in light mode by design.
 *   Per user feedback, the light surface now ALSO gets a live layer —
 *   just dialled down: lower particle count, much lower opacity, warm
 *   gold-on-cream palette instead of violet-on-black. Same primitives,
 *   different lighting.
 *
 * Theme detection: looks at `documentElement.classList.contains('dark')`
 * and re-renders when it changes (MutationObserver on <html>).
 *
 * Branding source: tenant doc { live_bg_preset, live_bg_video_url,
 * live_bg_opacity, primary_color, accent_color }. Reads from the
 * BrandingContext if mounted, else from `window.__VE_BRANDING__`
 * (set by BrandingProvider) so unauthenticated pages (Login, Marketing)
 * can still render the tenant's choice using the same component.
 */
import React, { useEffect, useRef, useState } from 'react';

function readBranding() {
  if (typeof window === 'undefined') return null;
  return window.__VE_BRANDING__ || null;
}

function useIsDark() {
  const [dark, setDark] = useState(() => {
    if (typeof document === 'undefined') return true;
    return document.documentElement.classList.contains('dark');
  });
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const obs = new MutationObserver(() => {
      setDark(document.documentElement.classList.contains('dark'));
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

function useBranding() {
  const [b, setB] = useState(() => readBranding());
  useEffect(() => {
    const handler = () => setB(readBranding());
    window.addEventListener('ve-branding-changed', handler);
    return () => window.removeEventListener('ve-branding-changed', handler);
  }, []);
  return b;
}

// ─── Preset: particles (default) ──────────────────────────────────────
function ParticlesPreset({ color, opacity, isDark = true }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d'); if (!ctx) return;
    let raf;
    const resize = () => { c.width = window.innerWidth; c.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);
    const count = Math.min(80, Math.floor(window.innerWidth / 24));
    // Light mode keeps dots near-original size and lines thin; we get
    // visibility from a higher-contrast color (luxe gold via the parent
    // export) + the 0.35 wrapper opacity. Earlier (v160) we overdid
    // dot scale + line width which made the layer feel like foreground.
    const dotScale = isDark ? 1.0 : 1.05;
    const lineWidth = isDark ? 0.4 : 0.5;
    const lineAlphaMul = isDark ? 0.5 : 0.55;
    const parts = Array.from({ length: count }, () => ({
      x: Math.random() * c.width,
      y: Math.random() * c.height,
      vx: (Math.random() - 0.5) * 0.25,
      vy: (Math.random() - 0.5) * 0.25,
      r: (Math.random() * 1.6 + 0.4) * dotScale,
    }));
    const loop = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      ctx.fillStyle = color;
      parts.forEach((p) => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > c.width) p.vx *= -1;
        if (p.y < 0 || p.y > c.height) p.vy *= -1;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill();
      });
      ctx.strokeStyle = color; ctx.lineWidth = lineWidth;
      for (let i = 0; i < parts.length; i++) {
        for (let j = i + 1; j < parts.length; j++) {
          const a = parts[i]; const b = parts[j];
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d < 130) {
            ctx.globalAlpha = (1 - d / 130) * lineAlphaMul;
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
          }
        }
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, [color, isDark]);
  return <canvas ref={ref} className="fixed inset-0 pointer-events-none" style={{ zIndex: 0, opacity }} aria-hidden="true" />;
}

// ─── Preset: starfield ────────────────────────────────────────────────
function StarfieldPreset({ color, opacity }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d'); if (!ctx) return;
    let raf;
    const resize = () => { c.width = window.innerWidth; c.height = window.innerHeight; };
    resize(); window.addEventListener('resize', resize);
    const stars = Array.from({ length: 220 }, () => ({
      x: Math.random() * c.width,
      y: Math.random() * c.height,
      z: Math.random() * 1 + 0.2,
      tw: Math.random() * Math.PI * 2,
    }));
    const loop = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      stars.forEach((s) => {
        s.tw += 0.02;
        const alpha = (Math.sin(s.tw) * 0.4 + 0.6) * s.z;
        ctx.fillStyle = color;
        ctx.globalAlpha = alpha;
        ctx.beginPath(); ctx.arc(s.x, s.y, s.z * 1.4, 0, Math.PI * 2); ctx.fill();
        s.y += s.z * 0.15;
        if (s.y > c.height) { s.y = 0; s.x = Math.random() * c.width; }
      });
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, [color]);
  return <canvas ref={ref} className="fixed inset-0 pointer-events-none" style={{ zIndex: 0, opacity }} aria-hidden="true" />;
}

// ─── Preset: aurora (CSS-only) ────────────────────────────────────────
function AuroraPreset({ color, accent, opacity }) {
  return (
    <div className="fixed inset-0 pointer-events-none ve-aurora" aria-hidden="true"
         style={{ zIndex: 0, opacity, '--bg-c1': color, '--bg-c2': accent || color }} />
  );
}

// ─── Preset: mesh (animated radial gradients) ─────────────────────────
function MeshPreset({ color, accent, opacity }) {
  return (
    <div className="fixed inset-0 pointer-events-none ve-mesh" aria-hidden="true"
         style={{ zIndex: 0, opacity, '--bg-c1': color, '--bg-c2': accent || color }} />
  );
}

// ─── Preset: video ────────────────────────────────────────────────────
function VideoPreset({ url, opacity }) {
  if (!url) return null;
  return (
    <video
      className="fixed inset-0 w-full h-full object-cover pointer-events-none"
      style={{ zIndex: 0, opacity }}
      src={url}
      autoPlay
      muted
      loop
      playsInline
      aria-hidden="true"
    />
  );
}

export default function LivingBackground({ forcePreset, forceColor, forceAccent, forceOpacity }) {
  const dark = useIsDark();
  const branding = useBranding();

  // v267 — Light mode now uses a warm champagne STARFIELD (was violet
  // particle network). Dark mode continues to honour the tenant's
  // branding-selected preset. In light mode we force `starfield` and
  // recolor to gold unless the caller has explicitly forced something.
  const brandingPreset = branding?.live_bg_preset || 'particles';
  const preset = forcePreset || (dark ? brandingPreset : 'starfield');
  if (preset === 'none') return null;

  const brandPrimary = forceColor || branding?.primary_color || '#AE52EA';
  const brandAccent  = forceAccent || branding?.accent_color || brandPrimary;

  // v267 — Light mode: warm champagne gold `#C9A227` reads as "stars on
  // cream" (matches the boardroom cream base). Dark mode keeps the
  // tenant's brand primary.
  const color  = dark ? brandPrimary : (forceColor || '#C9A227');
  const accent = dark ? brandAccent  : (forceAccent || '#D4AF37');

  // Light-mode starfield opacity 0.55 — enough to see the twinkle
  // against cream without overpowering copy. Dark mode unchanged.
  const defaultOpacity = dark ? 0.6 : 0.55;
  const opacity = typeof forceOpacity === 'number' ? forceOpacity
    : (typeof branding?.live_bg_opacity === 'number' ? branding.live_bg_opacity : defaultOpacity);

  switch (preset) {
    case 'starfield': return <StarfieldPreset color={color} opacity={opacity} />;
    case 'aurora':    return <AuroraPreset color={color} accent={accent} opacity={opacity} />;
    case 'mesh':      return <MeshPreset color={color} accent={accent} opacity={opacity} />;
    case 'video':     return <VideoPreset url={branding?.live_bg_video_url} opacity={opacity} />;
    case 'particles':
    default:          return <ParticlesPreset color={color} opacity={opacity} isDark={dark} />;
  }
}
